import { Component, OnInit } from '@angular/core';
import {
  ListingGroup,
  MerchantProfileCategory,
  MerchantProfileListItem,
  MerchantProfileStatus,
  MerchantProfileStatusLabels,
} from '../_models';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { GroupedListComponent } from '../_directives';
import { Location } from '@angular/common';
import {
  AlertService,
  CampaignService,
  MerchantCategoryService,
  MerchantProfileService,
} from '../_services';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { expandCollapse } from '../_shared';

@Component({
  selector: 'app-merchant-profiles',
  templateUrl: './merchant-profiles.component.html',
  styleUrls: [
    '../checkouts/checkout-profiles/grouped-listing.scss',
    './merchant-profiles.component.scss',
    '../checkouts/checkout-profiles/profiles-styles.component.scss',
  ],
  animations: [expandCollapse],
})
export class MerchantProfilesComponent
  extends GroupedListComponent<MerchantProfileListItem, MerchantProfileStatus>
  implements OnInit {
  public merchantProfileStatus = MerchantProfileStatus;
  protected groupLabel = 'Category';
  protected ungroupedName = 'Uncategorized';
  pageTitle = 'Create and manage your merchant profiles.';
  subtitle =
    'Group merchant profiles by product category, monitor verification status, and track approval performance across your portfolio.';

  featureParams: Params = {};

  constructor(
    protected router: Router,
    protected location: Location,
    protected route: ActivatedRoute,
    protected merchantProfileService: MerchantProfileService,
    protected merchantCategoryService: MerchantCategoryService,
    protected campaignService: CampaignService,
    protected alertService: AlertService,
    public ngxSmartModalService: NgxSmartModalService
  ) {
    super(router, location, route, merchantProfileService, alertService, merchantCategoryService, ngxSmartModalService);
    this.objectName = 'merchant profile';
  }

  ngOnInit() {
    ['resolve', 'gateway'].forEach((name) => {
      const value = this.route.snapshot.queryParams[name];

      if (value) {
        this.featureParams[name] = value;
      }
    });

    super.ngOnInit();
  }

  protected initializeFilter() {
    super.initializeFilter();
    this.filter['campaign'] = this.campaignService.getCurrentCampaignId();
  }

  protected runInitialQuery() {
    this.loadGroups();
  }

  // Category profiles carry only id/name/status, so rows come from the profile list and categories group them
  protected loadGroups() {
    this.loading = true;

    forkJoin([this.merchantCategoryService.list(), this.merchantProfileService.list(this.filter)])
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        (responses: [MerchantProfileCategory[], MerchantProfileListItem[]]) => {
          this.setGroups(responses[0], responses[1]);
          this.loading = false;
        },
        (error) => {
          this.handleError(error);
          this.loading = false;
        }
      );
  }

  protected getItemGroupId(profile: MerchantProfileListItem): number {
    return Number(profile.category);
  }

  getItemStatus(profile: MerchantProfileListItem): MerchantProfileStatus {
    return profile.status;
  }

  createNewMerchantProfile(group?: ListingGroup<MerchantProfileListItem>) {
    const queryParams: Params = { ...this.featureParams };

    if (group && !this.isUngrouped(group)) {
      queryParams.category = group.id;
    }

    this.router.navigate(['/merchant-profiles', 'new'], { queryParams });
  }

  navigateToMerchantProfile(id: number) {
    this.router.navigate(['/merchant-profiles', 'edit', id], { queryParams: this.featureParams });
  }

  getDisplayValue(value: string | null | undefined): string {
    let display = '-';

    if (value !== null && value !== undefined && String(value).length > 0) {
      display = String(value);
    }

    return display;
  }

  isArchived(profile: MerchantProfileListItem): boolean {
    return profile.status === MerchantProfileStatus.Archived;
  }

  getListStatusLabel(profile: MerchantProfileListItem): string {
    const labels = {};

    labels[MerchantProfileStatus.Active] = 'Verified';
    labels[MerchantProfileStatus.PendingVerification] = 'Pending';

    return labels[profile.status] ? labels[profile.status] : this.getStatusLabel(profile.status);
  }

  archiveProfile(profile: MerchantProfileListItem) {
    profile.showThreeDotMenu = false;

    if (window.confirm(`Are you sure you want to archive ${profile.name}?`)) {
      this.loading = true;
      this.merchantProfileService
        .delete(profile.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => {
            this.alertService.success(`Archived ${this.objectName}.`);
            this.loadGroups();
          },
          (error) => {
            this.handleError(error);
            this.loading = false;
          }
        );
    }
  }

  unarchiveProfile(profile: MerchantProfileListItem) {
    profile.showThreeDotMenu = false;

    if (window.confirm(`Are you sure you want to unarchive ${profile.name}?`)) {
      this.loading = true;
      this.merchantProfileService
        .unarchive(profile.id)
        .pipe(takeUntil(this._destroy$))
        .subscribe(
          () => {
            this.alertService.success(`Unarchived ${this.objectName}.`);
            this.loadGroups();
          },
          (error) => {
            this.handleError(error);
            this.loading = false;
          }
        );
    }
  }

  getStatusLabel(status: MerchantProfileStatus) {
    return MerchantProfileStatusLabels[status];
  }
}
